/**
 * Debounced analytics tracking hook.
 *
 * This hook provides a debounced version of analytics tracking that prevents
 * spam events and includes deduplication logic.
 */

import { useDebouncedFn, useLatestFn, useUnmount } from '@studio/react-utils/hooks';
import { useCallback, useRef } from 'react';
import { getDebounceConfig, shouldDebounceEvent } from '../config/debounce-config.js';
import type { EventCategory } from '../constants/event-categories.js';
import type { EventName } from '../constants/event-names.js';
import { track as originalTrack } from '../track.js';
import type { AnalyticsEvent, EventProperties } from '../types/index.js';
import { processEventDeduplication } from '../utils/event-deduplication.js';

interface DebouncedAnalyticsOptions {
  /** Override debounce delay for this hook instance */
  debounceOverride?: number;
  /** Override deduplication settings */
  deduplicationOverride?: boolean;
  /** Enable debug logging for this hook */
  debug?: boolean;
}

interface AnalyticsMetrics {
  totalEvents: number;
  debouncedEvents: number;
  duplicateEvents: number;
  trackedEvents: number;
}

/**
 * Hook for debounced analytics tracking with spam protection
 */
export function useDebouncedAnalytics(options: DebouncedAnalyticsOptions = {}) {
  const { debounceOverride, deduplicationOverride, debug = false } = options;

  // Metrics tracking
  const metricsRef = useRef<AnalyticsMetrics>({
    totalEvents: 0,
    debouncedEvents: 0,
    duplicateEvents: 0,
    trackedEvents: 0
  });

  // Map to store debounced functions per event type
  const debouncedFunctionsRef = useRef(new Map<string, ReturnType<typeof useDebouncedFn>>());

  /**
   * Log debug information if enabled
   */
  const logDebug = useLatestFn((message: string, data?: unknown) => {
    if (debug && process.env.NODE_ENV === 'development') {
      console.debug(`[DebouncedAnalytics] ${message}`, data);
    }
  });

  /**
   * Track an event immediately without debouncing
   */
  const trackImmediate = useLatestFn(async (event: AnalyticsEvent) => {
    metricsRef.current.totalEvents++;

    // Check for deduplication unless overridden
    if (deduplicationOverride !== false) {
      if (!processEventDeduplication(event)) {
        metricsRef.current.duplicateEvents++;
        logDebug('Event blocked by deduplication', { event: event.name });
        return;
      }
    }

    // Track the event
    await originalTrack(event);
    metricsRef.current.trackedEvents++;
    logDebug('Event tracked immediately', { event: event.name });
  });

  /**
   * Get or create a debounced function for a specific event type
   */
  const getDebouncedFunction = useLatestFn((eventName: string) => {
    if (!debouncedFunctionsRef.current.has(eventName)) {
      const config = getDebounceConfig(eventName);
      const debounceDelay = debounceOverride ?? config.debounce;

      const debouncedFn = useDebouncedFn((event: AnalyticsEvent) => {
        trackImmediate(event);
      }, debounceDelay);

      debouncedFunctionsRef.current.set(eventName, debouncedFn);
    }

    return debouncedFunctionsRef.current.get(eventName)!;
  });

  /**
   * Main track function with debouncing and deduplication
   */
  const track = useCallback(
    (name: EventName, category: EventCategory, properties?: EventProperties) => {
      const event: AnalyticsEvent = {
        name,
        category,
        ...(properties && { properties }),
        timestamp: Date.now()
      } as AnalyticsEvent;

      metricsRef.current.totalEvents++;
      logDebug('Track called', { event: name, category, properties });

      // Check if this event type should be debounced
      if (!shouldDebounceEvent(name)) {
        logDebug('Event not configured for debouncing, tracking immediately', { event: name });
        trackImmediate(event);
        return;
      }

      // Use debounced function for this event type
      const debouncedFn = getDebouncedFunction(name);
      metricsRef.current.debouncedEvents++;
      logDebug('Event debounced', { event: name, debounceDelay: getDebounceConfig(name).debounce });
      debouncedFn(event);
    },
    [debounceOverride, deduplicationOverride, debug]
  );

  /**
   * Track an event immediately, bypassing debouncing
   */
  const trackNow = useCallback(
    (name: EventName, category: EventCategory, properties?: EventProperties) => {
      const event: AnalyticsEvent = {
        name,
        category,
        ...(properties && { properties }),
        timestamp: Date.now()
      } as AnalyticsEvent;

      logDebug('TrackNow called', { event: name, category, properties });
      trackImmediate(event);
    },
    [deduplicationOverride, debug]
  );

  /**
   * Flush all pending debounced events
   */
  const flush = useCallback(() => {
    logDebug('Flushing all pending events');
    for (const debouncedFn of debouncedFunctionsRef.current.values()) {
      debouncedFn.flush?.();
    }
  }, []);

  /**
   * Cancel all pending debounced events
   */
  const cancel = useCallback(() => {
    logDebug('Cancelling all pending events');
    for (const debouncedFn of debouncedFunctionsRef.current.values()) {
      debouncedFn.cancel?.();
    }
  }, []);

  /**
   * Get current metrics
   */
  const getMetrics = useCallback((): AnalyticsMetrics => {
    return { ...metricsRef.current };
  }, []);

  /**
   * Reset metrics
   */
  const resetMetrics = useCallback(() => {
    metricsRef.current = {
      totalEvents: 0,
      debouncedEvents: 0,
      duplicateEvents: 0,
      trackedEvents: 0
    };
    logDebug('Metrics reset');
  }, []);

  // Cleanup on unmount
  useUnmount(() => {
    flush(); // Ensure pending events are tracked
    debouncedFunctionsRef.current.clear();
  });

  return {
    track,
    trackNow,
    flush,
    cancel,
    getMetrics,
    resetMetrics
  };
}

/**
 * Simple hook that returns a debounced track function
 * This is a convenience hook for components that just need basic debounced tracking
 */
export function useAnalyticsTrack(options: DebouncedAnalyticsOptions = {}) {
  const { track } = useDebouncedAnalytics(options);
  return track;
}

/**
 * Hook for tracking with custom debounce delay
 */
export function useAnalyticsTrackWithDelay(
  debounceDelay: number,
  options: Omit<DebouncedAnalyticsOptions, 'debounceOverride'> = {}
) {
  return useDebouncedAnalytics({
    ...options,
    debounceOverride: debounceDelay
  });
}

/**
 * Hook for immediate tracking with deduplication only
 */
export function useAnalyticsTrackImmediate(
  options: Omit<DebouncedAnalyticsOptions, 'debounceOverride'> = {}
) {
  return useDebouncedAnalytics({
    ...options,
    debounceOverride: 0
  });
}
